import {useCallback, useMemo} from "react";
import "./index.css";
import "../../../layout/BannerToolbar/index.css";
import cn from "classnames";
import BgTransformer from "../../../layout/BgTransformer";
import PixelationLayer from "../../../layout/PixelationLayer";
import ImageUpload from "../../../layout/ImageUpload";
import DraggableCursor from "../../../layout/DraggableCursor";
import {useSetting} from "../../../contexts/setting";
import {useImages} from "../../../contexts/images";
import {useBgUrl} from "hooks/useBgUrl";
import {useEdits} from "../../../contexts/edits";
import {useFieldColor} from "../../../hooks/useFieldColor";
import {FLIP_H, FLIP_V, PORTRAIT, SCREENSHOT, TRASH_BIN} from "../../../data/constants";

/**
 * Inline SVG icon for the cursor mode button in the toolbar.
 * Small arrow cursor matching the DraggableCursor shape.
 */
const CursorIcon = () => (
  <svg width="20" height="26" viewBox="0 0 48 64" fill="none">
    <path
      d="M4 2L44 36H22L16 62L4 2Z"
      fill="white"
      stroke="white"
      strokeWidth="3"
      strokeLinejoin="round"
    />
  </svg>
);

const Screenshot = ({id, zoom}) => {
  const {
    layoutOption,
    isBgToolsExpanded,
    isImageToolsExpanded,
    currentImageToolsId,
    editIsImageToolsExpanded,
    editCurrentImageToolsId,
    editIsBgToolsExpanded,
  } = useSetting();

  const {
    uploadImages,
    uploadX,
    uploadY,
    uploadFlip,
    editUploadX,
    editUploadY,
    editUploadFlip,
    removeUploadImages,
  } = useImages();
  const {url: bgUrl} = useBgUrl();

  const {subtitles, editSubtitles} = useEdits();
  const {color: cursorColor, cascade: cursorColorCascade} = useFieldColor("cursorLabel", id);

  const templateClass = cn("template", `layout-${layoutOption}`);
  const cursorKey = `cursor-${id}`;
  const secondaryImageKey = `overlay-${id}`;
  const isDualUploadLayout = +layoutOption === 27;
  const cursorText = subtitles?.[cursorKey] ?? "New Feature";

  // --- Active mode detection ---
  const isBgActive = isBgToolsExpanded?.[id] || false;
  const isImageActive = isImageToolsExpanded && currentImageToolsId === id;
  const isCursorActive = isImageToolsExpanded && currentImageToolsId === cursorKey;
  const isSecondaryImageActive = isImageToolsExpanded && currentImageToolsId === secondaryImageKey;
  const hasUploadedImage = !!(uploadImages?.[id] || uploadImages?.main);
  const hasSecondaryImage = !!uploadImages?.[secondaryImageKey];

  // --- Layer interactivity ---
  // Screenshot area interactive only when image mode is active
  const screenshotPassThrough = !isImageActive;
  const secondaryScreenshotPassThrough = !isSecondaryImageActive;
  // When BG tools are open, screenshot dropzone must also be transparent to clicks.
  const disableDropzoneInPassThrough = isBgActive;
  // Cursor draggable when: cursor mode active OR nothing active (default = cursor draggable)
  const isCursorInteractive =
    isCursorActive || (!isBgActive && !isImageActive && !isSecondaryImageActive);

  // --- Cursor state ---
  // Starting position in canvas pixels, scaled with the canvas (1920 -> 1280).
  const cursorX = uploadX?.[cursorKey] ?? 133;
  const cursorY = uploadY?.[cursorKey] ?? 133;
  const cursorFlipRaw = uploadFlip?.[cursorKey];
  const cursorFlip = useMemo(() => cursorFlipRaw ?? {x: false, y: false}, [cursorFlipRaw]);

  const secondaryImageFlipRaw = uploadFlip?.[secondaryImageKey];
  const secondaryImageFlip = useMemo(
    () => secondaryImageFlipRaw ?? {x: false, y: false},
    [secondaryImageFlipRaw]
  );

  const handleCursorMove = useCallback(
    (nx, ny) => {
      editUploadX(cursorKey, nx, false);
      editUploadY(cursorKey, ny, false);
    },
    [cursorKey, editUploadX, editUploadY]
  );

  const handleCursorTextChange = useCallback(
    (newText) => {
      editSubtitles(newText, cursorKey, true);
    },
    [cursorKey, editSubtitles]
  );

  // --- Image flip state ---
  const imageFlipRaw = uploadFlip?.[id] ?? uploadFlip?.main;
  const imageFlip = useMemo(() => imageFlipRaw ?? {x: false, y: false}, [imageFlipRaw]);

  // --- Toolbar actions ---
  const toggleMode = useCallback(
    (mode) => {
      const key = mode === "cursor" ? cursorKey : mode === "secondary" ? secondaryImageKey : id;
      const isAlreadyActive = isImageToolsExpanded && currentImageToolsId === key;
      if (isAlreadyActive) {
        editIsImageToolsExpanded(false);
        editCurrentImageToolsId(null);
      } else {
        editCurrentImageToolsId(key);
        editIsImageToolsExpanded(true);
        // Close BG tools when activating a toolbar mode
        editIsBgToolsExpanded(id, false);
      }
    },
    [
      id,
      cursorKey,
      secondaryImageKey,
      isImageToolsExpanded,
      currentImageToolsId,
      editIsImageToolsExpanded,
      editCurrentImageToolsId,
      editIsBgToolsExpanded,
    ]
  );

  const handleCursorFlip = useCallback(
    (axis) => {
      editUploadFlip(cursorKey, {...cursorFlip, [axis]: !cursorFlip[axis]});
    },
    [cursorKey, cursorFlip, editUploadFlip]
  );

  const handleImageFlip = useCallback(
    (axis) => {
      editUploadFlip(id, {...imageFlip, [axis]: !imageFlip[axis]});
    },
    [id, imageFlip, editUploadFlip]
  );

  const handleImageDelete = useCallback(() => {
    removeUploadImages(id, false);
    editIsImageToolsExpanded(false);
    editCurrentImageToolsId(null);
  }, [id, removeUploadImages, editIsImageToolsExpanded, editCurrentImageToolsId]);

  const handleSecondaryImageFlip = useCallback(
    (axis) => {
      editUploadFlip(secondaryImageKey, {
        ...secondaryImageFlip,
        [axis]: !secondaryImageFlip[axis],
      });
    },
    [secondaryImageKey, secondaryImageFlip, editUploadFlip]
  );

  const handleSecondaryImageDelete = useCallback(() => {
    removeUploadImages(secondaryImageKey, true);
    editIsImageToolsExpanded(false);
    editCurrentImageToolsId(null);
  }, [secondaryImageKey, removeUploadImages, editIsImageToolsExpanded, editCurrentImageToolsId]);

  return (
    <div id={id} className={templateClass} style={{"--zoom-level": zoom}}>
      {/* ── Template layers (MUST be first child — export reads children[0]) ── */}
      <div>
        {/* Layer 1: Product background */}
        <BgTransformer src={bgUrl} id={id} />
        <PixelationLayer />

        {/* Layer 2: Screenshot upload with rounded mask */}
        <div
          className={cn("screenshot-area", "screenshot-area--primary", {
            "pass-through": screenshotPassThrough,
            "pass-through-no-dropzone": disableDropzoneInPassThrough,
          })}
        >
          <ImageUpload id={id} className="screenshot-upload" coverFitKey={layoutOption} />
        </div>

        {/* Layer 3: Portrait upload (layout 27 only) */}
        {isDualUploadLayout && (
          <div
            className={cn("screenshot-area", "screenshot-area--secondary", {
              "pass-through": secondaryScreenshotPassThrough,
              "pass-through-no-dropzone": disableDropzoneInPassThrough,
            })}
          >
            <ImageUpload
              id={secondaryImageKey}
              imageSlotKey={secondaryImageKey}
              transformKey={secondaryImageKey}
              className="screenshot-upload"
              coverFitKey={layoutOption}
            />
          </div>
        )}

        {/* Layer 4: Decorative cursor */}
        <DraggableCursor
          x={cursorX}
          y={cursorY}
          flipX={cursorFlip.x}
          flipY={cursorFlip.y}
          isInteractive={isCursorInteractive}
          onMove={handleCursorMove}
          text={cursorText}
          color={cursorColor}
          colorCascade={cursorColorCascade}
          onTextChange={handleCursorTextChange}
          zoomLevel={zoom}
        />
      </div>

      {/* ── Screenshot toolbar (above template via absolute positioning) ── */}
      <div className="screenshot-toolbar">
        {/* Cursor mode */}
        <button
          title="Edit cursor"
          className={cn("stb-btn", {active: isCursorActive})}
          onClick={() => toggleMode("cursor")}
        >
          <CursorIcon />
        </button>

        {isCursorActive && (
          <div className="stb-tools">
            <button
              className="stb-btn"
              title="Flip horizontal"
              onClick={() => handleCursorFlip("x")}
            >
              <img src={FLIP_H} alt="" />
            </button>
            <button className="stb-btn" title="Flip vertical" onClick={() => handleCursorFlip("y")}>
              <img src={FLIP_V} alt="" />
            </button>
          </div>
        )}

        <div className="stb-separator" />

        {/* Image mode */}
        <button
          title={isDualUploadLayout ? "Edit left image" : "Edit screenshot"}
          className={cn("stb-btn", {active: isImageActive})}
          onClick={() => toggleMode("image")}
        >
          <img src={SCREENSHOT} alt="" />
        </button>

        {isImageActive && (
          <div className="stb-tools">
            <button
              className="stb-btn"
              title="Flip horizontal"
              onClick={() => handleImageFlip("x")}
            >
              <img src={FLIP_H} alt="" />
            </button>
            <button className="stb-btn" title="Flip vertical" onClick={() => handleImageFlip("y")}>
              <img src={FLIP_V} alt="" />
            </button>
            {hasUploadedImage && (
              <button className="stb-btn" title="Remove image" onClick={handleImageDelete}>
                <img src={TRASH_BIN} alt="" />
              </button>
            )}
          </div>
        )}

        {/* Right image mode (layout 27 only) */}
        {isDualUploadLayout && (
          <>
            <div className="stb-separator" />

            <button
              title="Edit right image"
              className={cn("stb-btn", {active: isSecondaryImageActive})}
              onClick={() => toggleMode("secondary")}
            >
              <img src={PORTRAIT} alt="" />
            </button>

            {isSecondaryImageActive && (
              <div className="stb-tools">
                <button
                  className="stb-btn"
                  title="Flip horizontal"
                  onClick={() => handleSecondaryImageFlip("x")}
                >
                  <img src={FLIP_H} alt="" />
                </button>
                <button
                  className="stb-btn"
                  title="Flip vertical"
                  onClick={() => handleSecondaryImageFlip("y")}
                >
                  <img src={FLIP_V} alt="" />
                </button>
                {hasSecondaryImage && (
                  <button
                    className="stb-btn"
                    title="Remove right image"
                    onClick={handleSecondaryImageDelete}
                  >
                    <img src={TRASH_BIN} alt="" />
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Screenshot;
